// src/pages/CategoryLocation.js
import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { Users, Sun, Moon } from 'lucide-react';
import { Card } from '../components/ui/card';
import { 
  Breadcrumb, 
  BreadcrumbList, 
  BreadcrumbItem, 
  BreadcrumbLink, 
  BreadcrumbPage, 
  BreadcrumbSeparator 
} from '../components/ui/breadcrumb';
import { Button } from '../components/ui/button';
import ProviderCard from '../components/ProviderCard';
import { Meteors } from '../components/ui/meteors';

const API_URL = process.env.REACT_APP_API_URL;

export default function CategoryLocation() {
  const { slug, location } = useParams();
  const navigate = useNavigate();

  const handleOrderClick = (provider) => {
    navigate(`/provider/${provider.slug}?order=true`);
  };

  // Dark Mode
  const [darkMode, setDarkMode] = useState(() => {
    return localStorage.getItem('darkMode') === 'true' ||
           document.documentElement.classList.contains('dark');
  });

  const toggleDarkMode = () => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    if (newMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('darkMode', 'true');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('darkMode', 'false');
    }
  };

  // Fetch providers for this category + location
  const { data, isLoading, error } = useQuery({
    queryKey: ['providers', slug, location],
    queryFn: async () => {
      const res = await axios.get(
        `${API_URL}/api/servicecategory/${slug}/${location}/providers/`
      );
      return res.data;
    },
  });

  // Fetch categories (needed by ProviderCard for min charge logic)
  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const res = await axios.get(`${API_URL}/api/servicecategory/`);
      return res.data;
    },
  });

  const categoryName = data?.category_name || slug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  const locationName = location.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

  const providers = data?.results || [];

  const pageTitle = `${categoryName} in ${locationName}, Kenya | Hire Trusted Providers – SErraND`;
  const pageDescription = `Find verified ${categoryName.toLowerCase()} in ${locationName}, Kenya. Compare trusted local providers, transparent pricing, and real trust scores on SErraND.`;
  const pageKeywords = `${categoryName.toLowerCase()} ${locationName} Kenya, hire ${categoryName.toLowerCase()} near me, trusted ${categoryName.toLowerCase()} ${locationName}, SErraND providers`;

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": `${categoryName} Services in ${locationName}`,
    "description": pageDescription,
    "areaServed": locationName,
    "provider": {
      "@type": "Organization",
      "name": "SErraND",
      "url": "https://www.serrand.org"
    }
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content={pageKeywords} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`https://www.serrand.org/categories/${slug}/${location}`} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://www.serrand.org/categories/${slug}/${location}`} />
        <meta property="og:site_name" content="SErraND" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <Meteors count={40} angle={220} />

      <div className="relative z-10 min-h-screen bg-background pt-8 pb-16">
        {/* Dark Mode Toggle */}
        <div className="max-w-7xl mx-auto px-4 flex justify-end mb-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleDarkMode}
            className="rounded-full"
          >
            {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </Button>
        </div>

        {/* Breadcrumb */}
        <div className="max-w-7xl mx-auto px-4 mb-8">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/">Home</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/categories">Categories</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to={`/categories/${slug}`}>{categoryName}</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{locationName}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>

        {/* Hero */}
        <section className="max-w-5xl mx-auto px-4 text-center mb-16">
          <h1 className="text-5xl md:text-6xl font-bold tracking-tight mb-6">
            {categoryName} in {locationName}
          </h1>
          <p className="text-2xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Looking for reliable {categoryName.toLowerCase()} in {locationName}? 
            SErraND connects you with trusted local professionals — from fundi maji to mama fua, 
            mechanics to chefs — all verified and ready to serve you.
          </p>
        </section>

        {/* Providers Grid */}
        <section className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between mb-10">
            <h2 className="text-3xl font-semibold tracking-tight flex items-center gap-3">
              <Users className="h-8 w-8 text-primary" />
              Available Providers in {locationName}
            </h2>
            <p className="text-muted-foreground">
              {providers.length} professionals found
            </p>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-20">
              <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : error ? (
            <Card className="p-12 text-center">
              <p className="text-destructive">Failed to load providers. Please try again.</p>
            </Card>
          ) : providers.length === 0 ? (
            <Card className="p-12 text-center">
              <p className="text-muted-foreground">No providers available in this area yet.</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {providers.map((provider) => (
                <ProviderCard
                  key={provider.slug}
                  provider={provider}
                  handleOrderClick={handleOrderClick}
                  categories={categories}
                />
              ))}
            </div>
          )}
        </section>

        {/* Bottom Links */}
        <section className="max-w-4xl mx-auto px-4 text-center mt-20 space-y-4">
          <Button asChild size="lg" className="rounded-full">
            <Link to={`/categories/${slug}`}>View All Locations for {categoryName}</Link>
          </Button>
          <Button asChild variant="outline" size="lg" className="rounded-full">
            <Link to="/categories">Browse All Categories</Link>
          </Button>
        </section>
      </div>
    </>
  );
}
